import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios } from "../../utils/auth";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Shield, RefreshCw, AlertTriangle, CheckCircle2, Clock, FlaskConical, Play, Trash2, Zap, Sliders, ArrowRightLeft } from "lucide-react";
import RulesPanel from "./RulesPanel";
import GuardianThreatSummary from "./GuardianThreatSummary";

const GOLD = "#C5A55A";

const TIER_COLORS = {
  watch: "bg-white/10 text-white/70",
  prewarn: "bg-amber-500/20 text-amber-300",
  freeze: "bg-red-500/20 text-red-400",
  high_value: "bg-cyan-500/20 text-cyan-300",
  cleared: "bg-emerald-500/15 text-emerald-400",
  confirmed_fraud: "bg-red-900/40 text-red-300",
};

export default function GuardianTab() {
  const navigate = useNavigate();
  const [subTab, setSubTab] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("guardian");
    return p === "rules" ? "rules" : "cases";
  });
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTier, setFilterTier] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        authAxios.get("/admin/fraud/stats"),
        authAxios.get(`/admin/fraud/cases?limit=50${filterStatus ? "&status=" + filterStatus : ""}${filterTier ? "&tier=" + filterTier : ""}`),
      ]);
      setStats(s.data);
      setCases(c.data.rows || []);
    } catch (err) {
      toast.error("Failed to load Guardian data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterStatus, filterTier]);

  // Deep-link to case — now navigates straight to the FraudDefender page
  // (choice 1b — single code path, modal removed).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get("case");
    if (cid) {
      authAxios.get(`/admin/fraud/cases/${cid}`).then((r) => {
        const uid = r?.data?.case?.user_id;
        if (uid) navigate(`/admin/fraud/defender/${uid}?case_id=${cid}`, { replace: true });
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section data-testid="guardian-section" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Shield className="w-4 h-4" style={{ color: GOLD }} /> Guardian
        </h2>
        <div className="flex items-center gap-2">
          {/* Sub-tab switcher */}
          <div className="flex bg-white/5 rounded-lg p-0.5">
            <button
              onClick={() => setSubTab("cases")}
              className={`px-3 py-1.5 text-xs rounded flex items-center gap-1.5 transition ${subTab === "cases" ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80"}`}
              data-testid="guardian-tab-cases"
            >
              <Shield className="w-3 h-3" /> Cases
            </button>
            <button
              onClick={() => setSubTab("rules")}
              className={`px-3 py-1.5 text-xs rounded flex items-center gap-1.5 transition ${subTab === "rules" ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80"}`}
              data-testid="guardian-tab-rules"
            >
              <Sliders className="w-3 h-3" /> Rules
            </button>
          </div>
          {subTab === "cases" && (
            <Button
              size="sm"
              variant="outline"
              onClick={load}
              disabled={loading}
              className="border-white/10 text-white/60 hover:text-white gap-1.5"
              data-testid="refresh-guardian"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          )}
        </div>
      </div>

      {subTab === "rules" && <RulesPanel />}
      {subTab === "cases" && <CasesView
        stats={stats} cases={cases} loading={loading} load={load}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        filterTier={filterTier} setFilterTier={setFilterTier}
        selectedCase={selectedCase} setSelectedCase={setSelectedCase}
        navigate={navigate}
      />}
    </section>
  );
}

function CasesView({ stats, cases, loading, load, filterStatus, setFilterStatus, filterTier, setFilterTier, selectedCase, setSelectedCase, navigate }) {
  return (
    <div className="space-y-6">
      {/* Extended threat summary — sits above the per-tier KPI row */}
      <GuardianThreatSummary />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3" data-testid="guardian-kpis">
        <KpiCard icon={AlertTriangle} label="Freeze open" value={stats?.open_freeze ?? "—"} color="text-red-400" testId="kpi-freeze" />
        <KpiCard icon={Clock} label="Prewarn open" value={stats?.open_prewarn ?? "—"} color="text-amber-300" testId="kpi-prewarn" />
        <KpiCard icon={Shield} label="Watch open" value={stats?.open_watch ?? "—"} color="text-white/70" testId="kpi-watch" />
        <KpiCard icon={AlertTriangle} label="High-value hold" value={stats ? `$${((stats.high_value_hold_cents || 0) / 100).toLocaleString()}` : "—"} color="text-cyan-300" testId="kpi-high-value" />
        <KpiCard icon={CheckCircle2} label="Cleared 30d" value={stats?.cleared_30d ?? "—"} color="text-emerald-400" testId="kpi-cleared" />
        <KpiCard icon={AlertTriangle} label="Confirmed 30d" value={stats?.confirmed_fraud_30d ?? "—"} color="text-red-300" testId="kpi-confirmed" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {["", "freeze", "prewarn", "watch", "high_value", "cleared", "confirmed_fraud"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 rounded text-xs ${filterStatus === s ? "bg-white/15 text-white" : "bg-white/5 text-white/50"}`}
            data-testid={`filter-status-${s || "all"}`}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {/* Case queue */}
      <Card className="bg-[#0a0a0f] border-white/5 p-5">
        <h3 className="text-sm font-semibold text-white/90 mb-3">Case Queue</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="case-queue-table">
            <thead className="text-white/40 uppercase tracking-wide">
              <tr>
                <th className="text-left py-2 px-2">Case</th>
                <th className="text-left py-2 px-2">Tier</th>
                <th className="text-left py-2 px-2">Threat</th>
                <th className="text-right py-2 px-2">Score</th>
                <th className="text-left py-2 px-2">Affiliate</th>
                <th className="text-left py-2 px-2">Trust</th>
                <th className="text-left py-2 px-2">Flags</th>
                <th className="text-right py-2 px-2">$ at risk</th>
                <th className="text-left py-2 px-2">Opened</th>
                <th className="text-left py-2 px-2">Auto-freeze</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr
                  key={c.case_id}
                  onClick={() => {
                    // Navigate to the consolidated FraudDefender page for
                    // this user, deep-linking the case_id so it auto-opens
                    // in the inline CaseDetailPanel. (Modal removed — one
                    // canonical code path.)
                    const uid = c.user_id;
                    if (!uid || uid === "unknown") {
                      // Fallback for admin_override / orphan cases with no user_id
                      authAxios.get(`/admin/fraud/cases/${c.case_id}`)
                        .then((r) => setSelectedCase(r.data))
                        .catch((err) => {
                          const code = err?.response?.status;
                          if (code === 401) toast.error("Session expired — please log in again");
                          else if (code === 404) toast.error("Case not found");
                          else toast.error(err?.response?.data?.detail || "Failed to load case");
                        });
                      return;
                    }
                    navigate(`/admin/fraud/defender/${uid}?case_id=${c.case_id}`);
                  }}
                  className="border-t border-white/[0.04] text-white/80 cursor-pointer hover:bg-white/[0.03]"
                  data-testid={`case-row-${c.case_id}`}
                >
                  <td className="py-2 px-2 font-mono text-white/60">{c.case_id.slice(0, 12)}</td>
                  <td className="py-2 px-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase ${TIER_COLORS[c.status] || TIER_COLORS.watch}`}>
                      {c.status}
                    </span>
                    {c.has_high_value && (
                      <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] bg-cyan-500/15 text-cyan-300" title="Contains high-value commission on hold">
                        💰 ${((c.high_value_amount_cents || 0) / 100).toFixed(0)}
                      </span>
                    )}
                    {c.trust_bias_flag && (
                      <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] bg-sky-500/15 text-sky-300" title="Trusted affiliate — admin may downgrade">
                        TRUSTED
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 font-mono">{c.threat_class}</td>
                  <td className="text-right py-2 px-2 font-mono" style={{ color: GOLD }}>{c.risk_score}</td>
                  <td className="py-2 px-2">
                    <div className="text-white/85">{c.affiliate?.name || "—"}</div>
                    <div className="text-white/40 text-[10px]">{c.affiliate?.email}</div>
                  </td>
                  <td className="py-2 px-2 font-mono text-white/60">{c.affiliate?.trust_score ?? "—"}</td>
                  <td className="py-2 px-2 text-white/50 text-[10px]">
                    {(c.flags || []).slice(0, 3).map((f) => f.code).join(", ")}
                    {c.flags?.length > 3 ? " +" + (c.flags.length - 3) : ""}
                  </td>
                  <td className="text-right py-2 px-2 font-mono">${((c.commissions_at_risk_cents || 0) / 100).toFixed(2)}</td>
                  <td className="py-2 px-2 text-white/40 text-[10px]">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                  <td className="py-2 px-2 text-white/40 text-[10px]">{c.auto_freeze_at ? new Date(c.auto_freeze_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {cases.length === 0 && (
                <tr><td colSpan="10" className="text-center text-white/30 py-6">No cases match this filter</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedCase && (
        <CaseDetailModal
          data={selectedCase}
          onClose={() => setSelectedCase(null)}
          onRefresh={async () => {
            const r = await authAxios.get(`/admin/fraud/cases/${selectedCase.case.case_id}`);
            setSelectedCase(r.data);
            load();
          }}
        />
      )}

      <BetaTestingTools onReload={load} />
    </div>
  );
}

function BetaTestingTools({ onReload }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState("");
  const [scoreResult, setScoreResult] = useState(null);
  const [scoreForm, setScoreForm] = useState({ buyer_user_id: "", affiliate_user_id: "", payment_id: "" });

  const call = async (label, path, body) => {
    setBusy(label);
    try {
      const r = await authAxios.post(path, body || {});
      toast.success(`${label} ✓`);
      onReload();
      return r.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${label} failed`);
    } finally {
      setBusy("");
    }
  };

  return (
    <Card className="bg-[#0a0a0f] border-cyan-500/15 p-5" data-testid="beta-testing-tools">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-cyan-300 text-sm font-medium hover:text-cyan-200"
        data-testid="toggle-beta-tools"
      >
        <FlaskConical className="w-4 h-4" />
        🧪 Beta Testing Tools
        <span className="text-white/40 text-[10px]">{expanded ? "▲ hide" : "▼ show"}</span>
      </button>
      {expanded && (
        <div className="mt-4 space-y-4">
          <p className="text-xs text-white/50">Refuses to run when <code>ENVIRONMENT=production</code>. All fixtures are prefixed <code>test_fraud_*</code> and cleanly removed by Reset.</p>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => call("Seed", "/admin/fraud/simulate/seed")} disabled={busy === "Seed"} className="bg-cyan-500 hover:bg-cyan-400 text-black gap-1.5" data-testid="btn-seed-fixtures">
              <Play className="w-3.5 h-3.5" /> Seed Fraud Fixtures (F1-F7)
            </Button>
            <Button size="sm" onClick={() => call("Force Tick", "/admin/fraud/simulate/force-tick")} disabled={busy === "Force Tick"} variant="outline" className="border-amber-500/30 text-amber-300 gap-1.5" data-testid="btn-force-tick">
              <Zap className="w-3.5 h-3.5" /> Force Scheduler Tick (F8)
            </Button>
            <Button size="sm" onClick={() => { if (window.confirm("Delete every test_fraud_* row?")) call("Reset", "/admin/fraud/simulate/reset"); }} disabled={busy === "Reset"} variant="outline" className="border-red-500/30 text-red-300 gap-1.5" data-testid="btn-reset-fixtures">
              <Trash2 className="w-3.5 h-3.5" /> Reset Fixtures
            </Button>
          </div>

          <div className="border-t border-white/5 pt-4">
            <div className="text-white/60 text-xs mb-2 flex items-center gap-2">
              <FlaskConical className="w-3 h-3" /> Score-a-Purchase Playground (F10) — no case opened
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
              <input placeholder="buyer_user_id" value={scoreForm.buyer_user_id} onChange={(e) => setScoreForm({ ...scoreForm, buyer_user_id: e.target.value })} className="bg-black/40 border border-white/10 rounded p-2 text-xs text-white" data-testid="score-buyer" />
              <input placeholder="affiliate_user_id" value={scoreForm.affiliate_user_id} onChange={(e) => setScoreForm({ ...scoreForm, affiliate_user_id: e.target.value })} className="bg-black/40 border border-white/10 rounded p-2 text-xs text-white" data-testid="score-aff" />
              <input placeholder="payment_id" value={scoreForm.payment_id} onChange={(e) => setScoreForm({ ...scoreForm, payment_id: e.target.value })} className="bg-black/40 border border-white/10 rounded p-2 text-xs text-white" data-testid="score-pid" />
            </div>
            <Button size="sm" onClick={async () => setScoreResult(await call("Score", "/admin/fraud/simulate/score-only", scoreForm))} disabled={busy === "Score"} className="bg-white/10 text-white gap-1.5" data-testid="btn-score-only">
              Introspect Score
            </Button>
            {scoreResult && (
              <pre className="mt-3 bg-black/60 border border-white/10 rounded p-3 text-[10px] text-white/70 overflow-auto max-h-64" data-testid="score-result">
                {JSON.stringify(scoreResult, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function KpiCard({ icon: Icon, label, value, color, testId }) {
  return (
    <Card className="bg-[#0a0a0f] border-white/5 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-white/40 text-[10px] uppercase tracking-wide">
        <Icon className={`w-3 h-3 ${color || ""}`} />
        {label}
      </div>
      <div className={`mt-2 text-xl font-mono ${color || "text-white/90"}`}>{value}</div>
    </Card>
  );
}

function CaseDetailModal({ data, onClose, onRefresh }) {
  const { case: c, affiliate, commissions } = data;
  const [note, setNote] = useState("");
  const [cascade, setCascade] = useState(false);
  const [busy, setBusy] = useState(false);

  const doAction = async (action) => {
    setBusy(true);
    try {
      await authAxios.post(`/admin/fraud/cases/${c.case_id}/action`, { action, note, cascade });
      toast.success(`Case ${action.replace(/_/g, " ")}`);
      await onRefresh();
      if (["clear", "confirm_fraud"].includes(action)) onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const doVouch = async () => {
    setBusy(true);
    try {
      await authAxios.post(`/admin/fraud/users/${c.user_id}/vouch`, { vouched: true, reason: note });
      toast.success("Affiliate vouched (12mo)");
      await onRefresh();
    } catch (err) {
      toast.error("Vouch failed");
    } finally {
      setBusy(false);
    }
  };

  const approveHighValue = async () => {
    setBusy(true);
    try {
      const r = await authAxios.post(`/admin/fraud/cases/${c.case_id}/approve-high-value`);
      toast.success(`Released ${r.data.released} row(s) for payout`);
      await onRefresh();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const splitHighValue = async () => {
    if (!window.confirm("Split this high-value commission into sub-$500 chunks and release for payout?")) return;
    setBusy(true);
    try {
      const r = await authAxios.post(`/admin/fraud/cases/${c.case_id}/split-high-value`, { chunk_cents: 49900 });
      toast.success(`Split into ${r.data.rows_split} row(s)`);
      await onRefresh();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Split failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="case-detail-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0a0a0a] border-b border-white/10 p-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-[10px] uppercase ${TIER_COLORS[c.status] || TIER_COLORS.watch}`}>{c.status}</span>
              <span className="text-white/90 font-mono">{c.case_id}</span>
              <span className="px-2 py-0.5 rounded text-[10px] bg-white/5 text-white/60">Threat {c.threat_class}</span>
            </div>
            <div className="text-white/50 text-xs mt-1">
              Risk <span style={{ color: GOLD }}>{c.risk_score}/100</span>
              &nbsp;·&nbsp; Trust <span className="text-white/80">{c.trust_score}/100</span>
              {c.trust_bias_flag && <span className="ml-2 text-sky-300 text-[10px]">TRUSTED — admin may downgrade</span>}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-case-modal">✕</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4 p-4">
          {/* admin_override cases don't have commissions/flags — show a
              banner so admins aren't confused by empty panels. */}
          {(c.opened_by === "admin_override" || (c.signals || []).includes("admin_override")) && (
            <div className="col-span-full rounded-lg border border-sky-500/30 bg-sky-500/10 p-3 text-xs text-sky-200">
              <strong>Admin-opened case.</strong> This case was created by a manual account_status change,
              not by machine fraud detection. Commissions, risk signals, and buyer tickets will be empty.
              The timeline below shows the admin action that triggered it.
            </div>
          )}
          {/* Left column */}
          <div className="space-y-4">
            {/* Affiliate */}
            <Card className="bg-[#0f0f0f] border-white/5 p-4" data-testid="affiliate-card">
              <div className="flex items-center justify-between mb-2">
                <div className="text-white/40 text-[10px] uppercase">Affiliate</div>
                <button
                  onClick={() => { window.location.href = `/plog/commission_ledger_v2?user_id=${encodeURIComponent(c.user_id)}`; }}
                  className="text-[10px] px-2 py-1 rounded bg-[#C5A55A]/10 hover:bg-[#C5A55A]/20 border border-[#C5A55A]/30 text-[#C5A55A] flex items-center gap-1"
                  data-testid="view-commissions-btn"
                  title="Open commission ledger filtered to this affiliate"
                >
                  <ArrowRightLeft className="w-3 h-3" /> View commissions
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-white/40">Name:</span> <span className="text-white">{affiliate.name}</span></div>
                <div><span className="text-white/40">Email:</span> <span className="text-white">{affiliate.email}</span></div>
                <div><span className="text-white/40">Aff ID:</span> <span className="text-white font-mono">{affiliate.aff_id}</span></div>
                <div><span className="text-white/40">Tier:</span> <span className="text-white">{affiliate.affiliate_compensation_profile || "STANDARD"}</span></div>
                <div><span className="text-white/40">Joined:</span> <span className="text-white">{affiliate.created_at ? new Date(affiliate.created_at).toLocaleDateString() : "—"}</span></div>
                <div><span className="text-white/40">Vouched:</span> <span className="text-white">{affiliate.admin_vouched_at ? new Date(affiliate.admin_vouched_at).toLocaleDateString() : "—"}</span></div>
              </div>
            </Card>

            {/* Dual panels: Risk vs Trust */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card className="bg-[#0f0f0f] border-red-500/20 p-4" data-testid="risk-signals-panel">
                <div className="text-red-400 text-[10px] uppercase mb-2">Risk Signals</div>
                <div className="space-y-2">
                  {(c.flags || []).map((f, i) => (
                    <div key={i} className="text-xs border-l-2 border-red-500/50 pl-2">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-mono">{f.code}</span>
                        <span className="text-red-400 font-mono">+{f.weight}</span>
                      </div>
                      <div className="text-white/40 text-[10px]">
                        {Object.entries(f.evidence || {}).map(([k, v]) => `${k}=${v}`).join(", ")}
                      </div>
                    </div>
                  ))}
                  {(c.flags || []).length === 0 && <div className="text-white/30 text-xs">None</div>}
                </div>
              </Card>

              <Card className="bg-[#0f0f0f] border-emerald-500/20 p-4" data-testid="trust-signals-panel">
                <div className="text-emerald-400 text-[10px] uppercase mb-2">Trust Signals</div>
                <div className="space-y-2">
                  {(c.innocent_flags || []).map((f, i) => (
                    <div key={i} className="text-xs border-l-2 border-emerald-500/50 pl-2">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-mono">{f.code}</span>
                        <span className="text-emerald-400 font-mono">{f.weight}</span>
                      </div>
                    </div>
                  ))}
                  {(c.innocent_flags || []).length === 0 && <div className="text-white/30 text-xs">None</div>}
                </div>
              </Card>
            </div>

            {/* Support tickets */}
            <Card className="bg-[#0f0f0f] border-white/5 p-4" data-testid="support-tickets-card">
              <div className="text-white/40 text-[10px] uppercase mb-2">Buyer Support Tickets (90d)</div>
              {(c.buyer_support_tickets || []).length === 0 && <div className="text-white/30 text-xs">No tickets from this buyer.</div>}
              {(c.buyer_support_tickets || []).map((t, i) => (
                <div key={i} className="text-xs py-1 border-b border-white/5 last:border-0">
                  <span className="text-white">{t.subject}</span>
                  <span className="text-white/40 ml-2">· {t.status}</span>
                  <span className="text-white/30 ml-2">· {t.created_at?.slice(0, 10)}</span>
                </div>
              ))}
            </Card>

            {/* Commissions */}
            <Card className="bg-[#0f0f0f] border-white/5 p-4">
              <div className="text-white/40 text-[10px] uppercase mb-2">Related Commissions</div>
              <table className="w-full text-xs">
                <thead className="text-white/40 uppercase text-[9px]">
                  <tr><th className="text-left">Payment</th><th className="text-left">Type</th><th className="text-right">Amount</th><th className="text-left">Status</th></tr>
                </thead>
                <tbody>
                  {commissions.map((r, i) => (
                    <tr key={i} className="border-t border-white/[0.04]">
                      <td className="text-white/70 font-mono text-[10px]">{r.payment_id?.slice(0, 24)}</td>
                      <td className="text-white/60">{r.commission_type}</td>
                      <td className="text-right font-mono text-white/80">${((r.commission_amount || 0) / 100).toFixed(2)}</td>
                      <td><span className={`px-1.5 py-0.5 rounded text-[9px] uppercase ${r.status === "FROZEN" ? "bg-red-500/20 text-red-400" : "bg-white/5 text-white/60"}`}>{r.status}</span></td>
                    </tr>
                  ))}
                  {commissions.length === 0 && <tr><td colSpan="4" className="text-center text-white/30 py-3">—</td></tr>}
                </tbody>
              </table>
            </Card>

            {/* Timeline */}
            <Card className="bg-[#0f0f0f] border-white/5 p-4" data-testid="timeline-card">
              <div className="text-white/40 text-[10px] uppercase mb-2">Timeline</div>
              <div className="space-y-2">
                {(c.timeline || []).map((t, i) => (
                  <div key={i} className="text-xs flex gap-3">
                    <span className="text-white/40 font-mono text-[10px]">{t.at?.slice(0, 19).replace("T", " ")}</span>
                    <span className="text-white/80">{t.event}</span>
                    <span className="text-white/40 text-[10px]">{t.actor}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Admin notes */}
            {(c.admin_notes || []).length > 0 && (
              <Card className="bg-[#0f0f0f] border-white/5 p-4">
                <div className="text-white/40 text-[10px] uppercase mb-2">Admin Notes</div>
                {c.admin_notes.map((n, i) => (
                  <div key={i} className="text-xs py-2 border-b border-white/5 last:border-0">
                    <div className="text-white/50 text-[10px]">{n.admin_id} · {n.at?.slice(0, 19)}</div>
                    <div className="text-white/90 mt-1">{n.note}</div>
                  </div>
                ))}
              </Card>
            )}
          </div>

          {/* Right action bar */}
          <div className="space-y-2 lg:sticky lg:top-4 lg:h-fit">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add note (optional)..."
              rows="3"
              className="w-full bg-black/40 border border-white/10 rounded p-2 text-xs text-white"
              data-testid="admin-note-input"
            />
            <label className="flex items-center gap-2 text-[11px] text-white/60">
              <input type="checkbox" checked={cascade} onChange={(e) => setCascade(e.target.checked)} data-testid="cascade-checkbox" />
              Cascade-freeze downline
            </label>

            <Button size="sm" onClick={() => doAction("clear")} disabled={busy} className="w-full bg-emerald-500/80 hover:bg-emerald-500 text-black" data-testid="action-clear">Clear (Legit)</Button>
            {c.has_high_value && (
              <>
                <Button size="sm" onClick={approveHighValue} disabled={busy} className="w-full bg-cyan-500 hover:bg-cyan-400 text-black" data-testid="action-approve-hv">Approve High-Value Payout</Button>
                <Button size="sm" onClick={splitHighValue} disabled={busy} variant="outline" className="w-full border-cyan-500/40 text-cyan-300" data-testid="action-split-hv">Split into &lt;$500 chunks</Button>
              </>
            )}
            <Button size="sm" onClick={() => doAction("extend")} disabled={busy} variant="outline" className="w-full border-white/15 text-white/70" data-testid="action-extend">+72h Window</Button>
            <Button size="sm" onClick={() => doAction("escalate")} disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 text-black" data-testid="action-escalate">Escalate → Freeze</Button>
            <Button size="sm" onClick={() => doAction("request_info")} disabled={busy} variant="outline" className="w-full border-sky-500/40 text-sky-300" data-testid="action-request">Request Info from User</Button>
            <Button size="sm" onClick={() => doAction("downgrade_tier")} disabled={busy} variant="outline" className="w-full border-white/15 text-white/70" data-testid="action-downgrade">Downgrade Tier</Button>
            <Button size="sm" onClick={() => doAction("confirm_fraud")} disabled={busy} className="w-full bg-red-600 hover:bg-red-500 text-white" data-testid="action-confirm">Confirm Fraud</Button>

            <div className="pt-3 border-t border-white/5 mt-3">
              <Button size="sm" onClick={doVouch} disabled={busy} variant="outline" className="w-full border-sky-500/40 text-sky-300" data-testid="action-vouch">Vouch Affiliate (12mo)</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
